import { Star, Play, ArrowRight } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import { Link } from "react-router-dom";
import avatarJas from "@/assets/avatar-jas.png";
import avatarJames from "@/assets/avatar-james.png";
import avatarKemi from "@/assets/avatar-kemi.png";
import avatarTara from "@/assets/avatar-tara.png";
import avatarMindset from "@/assets/avatar-mindset.png";
import avatarGabriel from "@/assets/avatar-gabriel.png";
import avatarAlexPark from "@/assets/avatar-alex-park.png";
import avatarRyanCooper from "@/assets/avatar-ryan-cooper.png";
import avatarDanielOkoro from "@/assets/avatar-daniel-okoro.png";

type Platform = "youtube" | "instagram" | "tiktok" | "linkedin";

const platformIcon = (p: Platform) => {
  switch (p) {
    case "youtube": return <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#FF0000" }}><Play className="w-3.5 h-3.5 text-white fill-white" /></div>;
    case "instagram": return <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }}><svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5"/></svg></div>;
    case "tiktok": return <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#000" }}><span className="text-white text-xs font-bold">♪</span></div>;
    case "linkedin": return <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#0A66C2" }}><span className="text-white text-xs font-bold">in</span></div>;
  }
};

interface Review {
  name: string;
  bio: string;
  platform: Platform;
  quote: string;
  hasVideo: boolean;
  highlight: boolean;
  date: string;
  avatar?: string;
  avatarLetter: string;
  avatarColor: string;
}

const reviews: Review[] = [
  { name: "Alex Park", bio: "Education creator · runs online tutoring", platform: "youtube", quote: "Upgraded to the annual Creator plan during that special offer and it's the best money I've spent on my business this year.\n\n5 AI tools. Monthly Q&A calls. Viral hook library.\n\nIt replaced like 3 separate subscriptions I was paying for.", hasVideo: false, highlight: false, date: "February 2026", avatar: avatarAlexPark, avatarLetter: "A", avatarColor: "#2563EB" },
  { name: "Kemi Adeyemi", bio: "Food Creator · 890 followers", platform: "tiktok", quote: "I've been posting recipes for 2 years and never made a penny from it. AI StoryCraft helped me turn my most-saved recipe into an ebook. $640 in 3 weeks. From 890 followers.", hasVideo: true, highlight: true, date: "February 28, 2026", avatar: avatarKemi, avatarLetter: "K", avatarColor: "#9B59B6" },
  { name: "Ryan Cooper", bio: "Fitness creator · runs a PT business", platform: "instagram", quote: "The Clipping AI is stupid good. I uploaded a 40 minute podcast and it pulled out 8 clips with captions already on them.\n\nPosted them across TikTok, Reels, and Shorts and one of them hit 50K views.", hasVideo: false, highlight: false, date: "February 2026", avatar: avatarRyanCooper, avatarLetter: "R", avatarColor: "#E91E63" },
  { name: "Jas Kaur", bio: "Fitness Creator · 1,200 followers", platform: "instagram", quote: "I had zero idea what to sell. AI StoryCraft literally built my first digital product for me — a fitness meal plan. I made $1,247 in 14 days. I cried. Actual tears.", hasVideo: true, highlight: false, date: "March 12, 2026", avatar: avatarJas, avatarLetter: "J", avatarColor: "#E74C3C" },
  { name: "Daniel Okoro", bio: "Finance creator · 14K YouTube subscribers", platform: "youtube", quote: "Been using AI StoryCraft for about 3 months now.\n\nNexis AI writes my scripts. Keyword Research finds my topics. Clipping AI turns one video into 6 shorts.\n\nWhat used to take me a full day now takes about 90 minutes.", hasVideo: false, highlight: true, date: "January 2026", avatar: avatarDanielOkoro, avatarLetter: "D", avatarColor: "#16A085" },
  { name: "Gabriel Santos", bio: "Marketing Coach · 14,000 followers", platform: "youtube", quote: "One product. One video. $4,200. I wish I'd found AI StoryCraft 2 years ago. The Nexis AI script tool alone is worth the subscription.", hasVideo: true, highlight: false, date: "February 10, 2026", avatar: avatarGabriel, avatarLetter: "G", avatarColor: "#2ECC71" },
];

export const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="relative" style={{ background: "#FFFFFF", padding: "80px 0" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-display mb-3" style={{ color: "#111827", fontSize: "clamp(28px, 4.5vw, 48px)", fontWeight: 800, lineHeight: 1.15 }}>
              Real Creators. Real <span style={{ color: "#F59E0B" }}>Results.</span>
            </h2>
            <p style={{ color: "#6B7280", fontSize: "16px", maxWidth: "520px", margin: "0 auto" }}>
              From 520 followers to 14,000 — every audience size is making money.
            </p>
          </div>
        </ScrollReveal>

        {/* Masonry Grid - matches /reviews page */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5 max-w-6xl mx-auto">
          {reviews.map((r, i) => (
            <ScrollReveal key={`${r.name}-${i}`} delay={i % 3 * 80}>
              <div
                className="break-inside-avoid rounded-2xl p-5"
                style={{
                  background: "#FFFFFF",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  border: "1px solid #F0F0F0",
                  borderLeft: r.highlight ? "3px solid #7C3AED" : "1px solid #F0F0F0",
                }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {r.avatar ? (
                      <img src={r.avatar} alt={r.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ background: r.avatarColor }}>
                        {r.avatarLetter}
                      </div>
                    )}
                    <div>
                      <p style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{r.name}</p>
                      <p style={{ fontSize: "11px", color: "#6B7280" }}>{r.bio}</p>
                    </div>
                  </div>
                  {platformIcon(r.platform)}
                </div>

                {/* Quote */}
                <div style={{ fontSize: "0.92rem", color: "#111827", lineHeight: 1.55, marginBottom: "14px" }}>
                  {r.quote.split('\n\n').map((paragraph, pi) => (
                    <p key={pi} className={pi > 0 ? "mt-3" : ""}>
                      {pi === 0 ? `"${paragraph}` : paragraph}
                      {pi === r.quote.split('\n\n').length - 1 ? '"' : ''}
                    </p>
                  ))}
                </div>

                {/* Video Placeholder */}
                {r.hasVideo && (
                  <div
                    className="rounded-xl mb-4 flex items-center justify-center overflow-hidden"
                    style={{
                      aspectRatio: "16/9",
                      background: "linear-gradient(135deg, #7C3AED, #9F67FF)",
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 ml-1" style={{ color: "#7C3AED", fill: "#7C3AED" }} />
                    </div>
                  </div>
                )}

                {/* Date */}
                <p style={{ fontSize: "12px", color: "#9CA3AF" }}>{r.date}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Discover Wall of Results */}
        <div className="flex justify-center mt-10">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 font-semibold transition-colors hover:opacity-80"
            style={{ color: "#7C3AED", fontSize: "16px" }}
          >
            <ArrowRight className="w-5 h-5" />
            <span>Discover Wall of Results</span>
          </Link>
        </div>
      </div>
    </section>
  );
};
